import React from 'react';
import { useTranslation } from 'react-i18next';
import { FallbackProps } from 'react-error-boundary';
import { NormalizedError } from '@/api/error/errorNormalizer';
import {
  Box,
  Typography,
  Button,
  Paper,
  List,
  ListItem,
  ListItemText,
  Divider,
} from '@mui/material';

/**
 * Error fallback component that renders when an error is caught by ErrorBoundary
 */
export const ErrorFallback: React.FC<FallbackProps> = ({
  error,
  resetErrorBoundary,
}) => {
  const { t } = useTranslation();
  // Check if this is one of our normalized errors
  const errorObj = error instanceof Error ? error : new Error(String(error));
  const isNormalized = typeof error === 'object' && error !== null && 'category' in error && 'errorId' in error;
  const normalizedError = isNormalized ? (error as NormalizedError) : null;

  return (
    <Paper
      elevation={3}
      sx={{
        p: 3,
        m: 2,
        maxWidth: 600,
        mx: 'auto',
      }}
    >
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          {t('error-fallback.title', { defaultValue: 'Something went wrong' })}
        </Typography>

        {normalizedError ? (
          <>
            <Typography variant="body1" color="error" sx={{ mb: 2 }}>
              {normalizedError.message}
            </Typography>

            <Box sx={{ textAlign: 'left', mb: 2 }}>
              <Typography variant="body1">
                {t('error-fallback.category', { defaultValue: 'Category' })}:{' '}
                <strong>{normalizedError.category}</strong>
              </Typography>

              {normalizedError.status && (
                <Typography variant="body1">
                  {t('error-fallback.status', { defaultValue: 'Status' })}:{' '}
                  <strong>{normalizedError.status}</strong>
                </Typography>
              )}

              {normalizedError.errorId && (
                <Typography variant="body1">
                  {t('error-fallback.error-id', { defaultValue: 'Error ID' })}:{' '}
                  <Box component="span" sx={{ fontFamily: 'monospace' }}>
                    {normalizedError.errorId}
                  </Box>
                </Typography>
              )}

              {normalizedError.validationErrors && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="h6">
                    {t('error-fallback.validation-errors', {
                      defaultValue: 'Validation Errors',
                    })}
                    :
                  </Typography>
                  <List>
                    {Object.entries(normalizedError.validationErrors).map(
                      ([field, errors]) => (
                        <ListItem
                          key={field}
                          sx={{
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                          }}
                        >
                          <ListItemText
                            primary={
                              <Typography variant="body1">
                                <strong>{field}:</strong>
                              </Typography>
                            }
                          />
                          <List sx={{ pl: 2 }}>
                            {errors.map((err, i) => (
                              <ListItem key={i}>
                                <ListItemText primary={err} />
                              </ListItem>
                            ))}
                          </List>
                        </ListItem>
                      ),
                    )}
                  </List>
                </Box>
              )}
            </Box>
          </>
        ) : (
          <Typography variant="body1" color="error" sx={{ mb: 2 }}>
            {errorObj.message}
          </Typography>
        )}

        <Divider sx={{ my: 2 }} />

        <Button variant="contained" onClick={resetErrorBoundary} sx={{ mt: 2 }}>
          {t('error-fallback.try-again', { defaultValue: 'Try Again' })}
        </Button>
      </Box>
    </Paper>
  );
};

export default ErrorFallback;
